/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect, useMemo } from "react";
import { n, groupVariantsByColor, getVariantColorHex } from "../../theme1/utils/theme1Helpers";
import { formatNumber } from "utils/formatNumber";
import { getCardGalleryImages } from "../../../utils/productMainImages";
import MainImageCarousel from "../../components/MainImageCarousel";
import Swal from "sweetalert2";

export default function ProductsSection({
  theme,
  filteredProducts = [],
  viewMode = "grid",
  setSelected,
  handleAddToCart,
}) {
  const safeTheme = theme || { primary: "#EC4899", accent: "#FCD34D" };

  return (
    <section className="w-full py-8 sm:py-16 px-3 sm:px-4 md:px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-6 sm:mb-12 space-y-2 sm:space-y-4">
          <h2
            className="text-2xl sm:text-4xl md:text-5xl font-black"
            style={{ color: safeTheme.primary }}
          >
            ✨ المعرض المميز
          </h2>
          <p className="text-white/40 text-xs sm:text-sm md:text-base max-w-lg mx-auto">
            اكتشف أحدث التشكيلات والمنتجات الفاخرة التي اخترناها لك بعناية فائقة
          </p>
        </div>

        {filteredProducts.length === 0 ? (
          <div className="text-center py-16 sm:py-24 text-white/20 border-2 border-dashed border-white/5 rounded-[2rem] sm:rounded-[3rem]">
            <div className="text-5xl sm:text-6xl mb-4 opacity-10">📦</div>
            <div className="text-base sm:text-xl">لا توجد منتجات متاحة حالياً في هذا القسم</div>
          </div>
        ) : (
          <div className={`
            ${viewMode === "grid"
              ? "grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6 md:gap-10"
              : "flex flex-col gap-4 sm:gap-6"
            }
          `}>
            {filteredProducts.map((product) => (
              <ProductItem
                key={product.id}
                product={product}
                theme={safeTheme}
                viewMode={viewMode}
                setSelected={setSelected}
                handleAddToCart={handleAddToCart}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ProductItem({ product, theme, viewMode, setSelected, handleAddToCart }) {
  const [selectedColorHex, setSelectedColorHex] = useState(null);
  const [selectedSizeLabel, setSelectedSizeLabel] = useState(null);

  const colorGroups = useMemo(() => groupVariantsByColor(product?.variants), [product]);
  const uniqueColors = useMemo(() => Object.values(colorGroups), [colorGroups]);
  const displayColors = useMemo(() => uniqueColors.filter((c) => c.hasColor), [uniqueColors]);

  const hasStockInGroup = (group) => {
    if (!group?.variants?.length) return false;
    return group.variants.some(v => {
      const vSizes = v.options || v.sizes || [];
      if (vSizes.length === 0) return n(v.stockQty) > 0;
      const hasSizeStock = vSizes.some(s => n(s.stock || s.stockQty) > 0);
      return hasSizeStock || n(v.stockQty) > 0;
    });
  };

  // 2. Initial Selection
  useEffect(() => {
    if (uniqueColors.length > 0) {
      const firstColor = uniqueColors.find(c => hasStockInGroup(c)) || uniqueColors[0];
      setSelectedColorHex(firstColor.hex);

      // Select first available size for this color
      const allSizes = [];
      firstColor.variants.forEach(v => {
        const vSizes = v.options || v.sizes || [];
        vSizes.forEach(s => allSizes.push({ label: s.label || s.size, stock: n(s.stock || s.stockQty) }));
      });
      const firstInStock = allSizes.find(s => s.stock > 0) || allSizes[0];
      if (firstInStock) setSelectedSizeLabel(firstInStock.label);
    }
  }, [product, uniqueColors]);

  // 3. Get Active Variant and Sizes
  const activeColorGroup = colorGroups[selectedColorHex] || uniqueColors[0];

  const availableSizes = useMemo(() => {
    if (!activeColorGroup) return [];
    const sizeMap = new Map();
    activeColorGroup.variants.forEach(v => {
      const vSizes = v.options || v.sizes || [];
      vSizes.forEach(s => {
        const label = s.label || s.size;
        let stock = n(s.stock || s.stockQty);
        if (stock <= 0 && n(v.stockQty) > 0) stock = n(v.stockQty);
        if (!sizeMap.has(label)) {
          sizeMap.set(label, { label, stock, variant: v });
        } else {
          // If size exists in multiple variants (unlikely but possible), add stock
          const existing = sizeMap.get(label);
          existing.stock += stock;
        }
      });
    });
    return Array.from(sizeMap.values());
  }, [activeColorGroup]);

  const activeSizeObj = useMemo(() => {
    return availableSizes.find(s => s.label === selectedSizeLabel) || availableSizes[0];
  }, [availableSizes, selectedSizeLabel]);

  const selectedVariant = activeSizeObj?.variant || activeColorGroup?.variants[0];
  const galleryImages = useMemo(
    () => getCardGalleryImages(product, selectedVariant?.imageUrl),
    [product, selectedVariant]
  );

  // 4. Price Logic
  const priceDetails = useMemo(() => {
    if (!selectedVariant) return { base: 0, discount: 0, final: 0 };
    const basePrice = Number(selectedVariant.price || 0);
    const dValue = Number(product.discountValue || 0);
    const dType = Number(product.discountType || 0);
    let discountAmount = 0;
    let finalPrice = basePrice;
    if (dValue > 0) {
      if (dType === 1) discountAmount = (basePrice * dValue) / 100;
      else discountAmount = dValue;
      finalPrice = basePrice - discountAmount;
    } else {
      finalPrice = Number(selectedVariant.finalPrice || selectedVariant.price || 0);
      discountAmount = basePrice - finalPrice;
    }
    return { base: basePrice, discount: discountAmount, final: Math.max(0, finalPrice) };
  }, [selectedVariant, product.discountValue, product.discountType]);

  const isOutOfStock = useMemo(() => {
    if (availableSizes.length === 0) return Number(selectedVariant?.stockQty || 0) <= 0;
    return availableSizes.every(s => s.stock <= 0);
  }, [availableSizes, selectedVariant]);

  const isProductOutOfStock = useMemo(() => {
    if (!uniqueColors.length) return Number(selectedVariant?.stockQty || 0) <= 0;
    return uniqueColors.every(c => !hasStockInGroup(c));
  }, [uniqueColors, selectedVariant]);

  const showOutOfStockAlert = () => {
    Swal.fire({
      icon: "info",
      title: "انتهى من المخزون",
      text: "هذا المنتج غير متوفر حالياً"
    });
  };

  const openDetails = () => {
    if (isProductOutOfStock) {
      showOutOfStockAlert();
      return;
    }
    setSelected(product);
  };

  const handleAddClick = (e) => {
    e.stopPropagation();
    if (isOutOfStock) { showOutOfStockAlert(); return; }
    if (!selectedVariant) { Swal.fire({ title: 'تنبيه', text: 'يرجى اختيار المنتج أولاً', icon: 'warning' }); return; }

    handleAddToCart({
      variantId: selectedVariant.variantId,
      price: priceDetails.final,
      basePrice: priceDetails.base,
      discount: priceDetails.discount,
      color: getVariantColorHex(selectedVariant),
      colorName: getVariantColorHex(selectedVariant),
      size: selectedSizeLabel || "",
      imageUrl: selectedVariant.imageUrl || product.mainImage,
      name: product.name,
      stockQty: activeSizeObj ? activeSizeObj.stock : Number(selectedVariant.stockQty || 0)
    }, 1);
  };

  const handleColorSelect = (hex) => {
    setSelectedColorHex(hex);
    const group = colorGroups[hex];
    const allSizes = [];
    group?.variants?.forEach(v => {
      const vSizes = v.options || v.sizes || [];
      vSizes.forEach(s => allSizes.push({ label: s.label || s.size, stock: n(s.stock || s.stockQty) }));
    });
    const firstInStock = allSizes.find(s => s.stock > 0) || allSizes[0];
    setSelectedSizeLabel(firstInStock?.label || null);
  };

  if (!selectedVariant) return null;

  if (viewMode === "list") {
    return (
      <div
        className={`group relative backdrop-blur-xl border rounded-[2rem] overflow-hidden transition-all duration-500 cursor-pointer flex flex-col md:flex-row p-4 gap-6 ${isProductOutOfStock
          ? 'bg-black/30 border-white/10 opacity-80'
          : 'bg-black/40 border-white/5 hover:border-white/20 hover:shadow-2xl'
          }`}
        onClick={openDetails}
      >
        {/* Image - List Mode */}
        <div className="w-full md:w-48 aspect-square bg-white/5 rounded-2xl flex items-center justify-center p-4 relative overflow-hidden group">
          <MainImageCarousel
            images={galleryImages}
            alt={product.name}
            variant="dark"
            compact
            imgClassName="w-full h-full object-contain filter drop-shadow-2xl group-hover:scale-110 transition-transform duration-700"
          />
          {priceDetails.discount > 0 && (
            <div className="absolute top-2 right-2 bg-red-600 text-[8px] font-black text-white px-2 py-0.5 rounded-lg shadow-lg z-10">
              -{product.discountType === 1 ? `${product.discountValue}%` : 'SALE'}
            </div>
          )}
          {isProductOutOfStock && (
            <div className="absolute inset-0 bg-black/45 flex items-center justify-center z-20">
              <span className="px-3 py-1 rounded-lg bg-white/90 text-gray-800 text-[10px] font-black">انتهى المخزون</span>
            </div>
          )}

        </div>

        {/* Info - List Mode */}
        <div className="flex-1 flex flex-col justify-between py-2">
          <div className="space-y-3">
            <div className="flex justify-between items-start gap-4">
              <div className="space-y-1">
                {/* Tags */}
                {product.tags && product.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-1">
                    {product.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 rounded-md text-[8px] font-bold"
                        style={{ background: `${theme.primary}22`, color: theme.primary }}
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
                <h3 className="font-bold text-xl text-white group-hover:text-purple-400 transition-colors">
                  {product.name}
                </h3>
                <div className="flex flex-col gap-0.5 mb-1">
                  {(product.category || product.subCategory) && (
                    <div className="flex items-center gap-2">
                      {product.category && (
                        <span className="text-[9px] font-black uppercase tracking-widest text-purple-400">
                          {product.category}
                        </span>
                      )}
                      {product.category && product.subCategory && <span className="text-white/10 text-[8px]">•</span>}
                      {product.subCategory && (
                        <span className="text-[9px] font-bold text-white/30 uppercase tracking-widest">
                          {product.subCategory}
                        </span>
                      )}
                    </div>
                  )}
                  <p className="text-[10px] text-white/30 uppercase font-black tracking-widest">
                    {product.brand || "خيارات متعددة متاحة"}
                  </p>
                </div>
              </div>
              <div className="text-left">
                <div className="text-2xl font-black text-white">
                  {formatNumber(priceDetails.final)} <span className="text-xs opacity-40">د.ع</span>
                </div>
                {priceDetails.discount > 0 && (
                  <div className="text-sm text-white/20 line-through font-bold">
                    {formatNumber(priceDetails.base)}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-6">
              {/* Color Switcher - Unique Colors ONLY */}
              {displayColors.length > 1 && (
                <div className="flex gap-2">
                  {displayColors.map((c) => (
                    <button
                      key={c.hex}
                      onClick={(e) => { e.stopPropagation(); handleColorSelect(c.hex); }}
                      className={`w-5 h-5 rounded-full border-2 transition-all ${selectedColorHex === c.hex ? 'scale-125 border-white shadow-lg' : 'border-white/20'}`}
                      style={{ background: c.hex }}
                    />
                  ))}
                </div>
              )}

              {/* Size Selector - Aggregated Sizes */}
              {availableSizes.length > 0 && (
                <div className="flex gap-1.5 font-ar">
                  {availableSizes.map((s, i) => (
                    <button
                      key={i}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (s.stock <= 0) {
                          showOutOfStockAlert();
                          return;
                        }
                        setSelectedSizeLabel(s.label);
                      }}
                      className={`px-3 py-1 rounded-lg text-[10px] font-bold transition-all ${selectedSizeLabel === s.label ? 'bg-white text-black' : 'bg-white/5 text-white/40'}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4 mt-6">
            <button
              onClick={handleAddClick}
              disabled={isOutOfStock}
              className="flex-1 max-w-[200px] py-3.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all hover:brightness-110 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed shadow-xl shadow-purple-900/10"
              style={{
                background: isOutOfStock ? 'rgba(255,255,255,0.05)' : `linear-gradient(135deg, ${theme.primary}, ${theme.accent || theme.primary})`,
                color: isOutOfStock ? 'rgba(255,255,255,0.2)' : '#000'
              }}
            >
              {isOutOfStock ? "نفذت الكمية" : "أضف للسلة"}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); openDetails(); }}
              className="px-6 py-3.5 rounded-xl border border-white/10 text-white/40 text-xs font-bold hover:bg-white/5 hover:text-white transition-all"
            >
              تفاصيل أكثر
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`
        group relative backdrop-blur-xl
        border rounded-2xl sm:rounded-[2.5rem] overflow-hidden
        transition-all duration-500 cursor-pointer flex flex-col h-full
        ${isProductOutOfStock
          ? 'bg-black/30 border-white/10 opacity-80'
          : 'bg-black/40 border-white/5 hover:border-white/20 hover:shadow-[0_20px_50px_rgba(0,0,0,0.5)]'
        }
      `}
      onClick={openDetails}
    >
      {/* Discount Badge */}
      {priceDetails.discount > 0 && (
        <div className="absolute top-3 right-3 sm:top-6 sm:right-6 z-10 bg-red-600 text-white px-2.5 py-1 sm:px-4 sm:py-1.5 rounded-lg sm:rounded-xl font-black text-[9px] sm:text-[10px] uppercase tracking-widest shadow-xl shadow-red-900/40">
          - {product.discountType === 1 ? `${product.discountValue}%` : 'SALE'}
        </div>
      )}
      {isProductOutOfStock && (
        <div className="absolute inset-0 bg-black/45 flex items-center justify-center z-20">
          <span className="px-3 py-1 rounded-lg bg-white/90 text-gray-800 text-[10px] font-black">انتهى المخزون</span>
        </div>
      )}


      {/* Image Container */}
      <div className="relative aspect-square bg-white/5 flex items-center justify-center p-3 sm:p-8 overflow-hidden">
        <MainImageCarousel
          images={galleryImages}
          alt={product.name}
          variant="dark"
          imgClassName="w-full h-full object-contain filter drop-shadow-2xl group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end justify-center pb-6 pointer-events-none">
          <span className="text-[10px] text-white/60 font-black uppercase tracking-[0.3em]">عرض التفاصيل</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 sm:p-6 flex flex-col flex-1 space-y-2 sm:space-y-4">
        <div className="space-y-1">
          {/* Tags */}
          {product.tags && product.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-1">
              {product.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-2 py-0.5 rounded-md text-[8px] font-bold"
                  style={{ background: `${theme.primary}22`, color: theme.primary }}
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
          <h3 className="font-bold text-sm sm:text-lg text-white group-hover:text-purple-400 transition-colors line-clamp-2 min-h-[2.4em] sm:min-h-[3rem]">
            {product.name}
          </h3>
          <div className="flex flex-col gap-0.5">
            {(product.category || product.subCategory) && (
              <div className="flex items-center gap-2">
                {product.category && (
                  <span className="text-[9px] font-black uppercase tracking-widest text-purple-400">
                    {product.category}
                  </span>
                )}
                {product.category && product.subCategory && <span className="text-white/10 text-[8px]">•</span>}
                {product.subCategory && (
                  <span className="text-[9px] font-bold text-white/30 uppercase tracking-widest">
                    {product.subCategory}
                  </span>
                )}
              </div>
            )}
            <p className="text-[10px] text-white/30 uppercase font-black tracking-widest">
              {product.brand || "خيارات متعددة متاحة"}
            </p>
          </div>
        </div>

        {/* Color Switcher - Unique Colors ONLY */}
        {displayColors.length > 1 && (
          <div className="hidden sm:flex flex-wrap gap-2 py-1">
            {displayColors.map((c) => (
              <button
                key={c.hex}
                onClick={(e) => { e.stopPropagation(); handleColorSelect(c.hex); }}
                className={`w-6 h-6 rounded-full border-2 transition-all ${selectedColorHex === c.hex ? 'scale-125 border-white shadow-lg shadow-white/40' : 'border-white/30 hover:border-white/70 shadow-sm'}`}
                style={{ background: c.hex }}
              />
            ))}
          </div>
        )}

        {/* Size Selector - Aggregated Sizes */}
        {availableSizes.length > 0 && (
          <div className="hidden sm:flex flex-wrap gap-1.5 pt-1">
            {availableSizes.map((s, i) => (
              <button
                key={i}
                onClick={(e) => {
                  e.stopPropagation();
                  if (s.stock <= 0) {
                    showOutOfStockAlert();
                    return;
                  }
                  setSelectedSizeLabel(s.label);
                }}
                className={`
                  px-2.5 py-1 rounded-lg text-[9px] font-black transition-all
                  ${selectedSizeLabel === s.label
                    ? 'bg-white text-black border-white'
                    : s.stock <= 0 ? 'opacity-20 cursor-not-allowed border-transparent text-white/20' : 'bg-white/5 text-white/40 border-white/5 hover:border-white/20 hover:text-white'}
                  border
                `}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        {/* Price Section */}
        <div className="mt-auto pt-2 sm:pt-4 flex items-baseline gap-2 sm:gap-3">
          <span className="text-base sm:text-2xl font-black text-white">
            {formatNumber(priceDetails.final)} <span className="text-xs opacity-40 font-normal">د.ع</span>
          </span>
          {priceDetails.discount > 0 && (
            <span className="text-sm text-white/20 line-through font-bold">
              {formatNumber(priceDetails.base)}
            </span>
          )}
        </div>

        {/* Add Button */}
        <button
          onClick={handleAddClick}
          disabled={isOutOfStock}
          className={`
            w-full py-2.5 sm:py-4 rounded-xl sm:rounded-2xl font-black text-[10px] sm:text-xs uppercase tracking-widest
            transition-all duration-300 transform active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed
            shadow-xl shadow-purple-900/10
          `}
          style={{
            background: isOutOfStock ? 'rgba(255,255,255,0.05)' : `linear-gradient(135deg, ${theme.primary}, ${theme.accent || theme.primary})`,
            color: isOutOfStock ? 'rgba(255,255,255,0.2)' : '#000'
          }}
        >
          {isOutOfStock ? "نفذت الكمية" : "إضافة للسلة"}
        </button>
      </div>
    </div>
  );
}
